// =============================================================================
// VandieZ agent-web -- Payments (/payments)
// =============================================================================
// FSD p61 + p68. Wallet balance, received vs pending, recent transactions,
// payout account.
//
// p68's rule is the one that shapes this screen: "keep customer-side and
// driver-side transactions as separate ledger lines linked to the same load
// ID". So the transactions table shows GROSS and COMMISSION as separate
// columns rather than a single net figure — an agent reconciling with a
// customer needs the number the customer paid, and reconciling with the
// platform needs the number it took. One netted column serves neither.
//
// PENDING PAYOUTS ARE NOT ADDED TO ANYTHING. The server has already debited
// them from `balance` via the payout_initiated ledger row; they are shown so
// the agent can see money in flight, and the copy says so, because "pending"
// next to a balance invites exactly the wrong mental arithmetic.
// =============================================================================

import { AlertCircle, Building2, RefreshCw, Wallet } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { accountApi } from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type {
  AgentProfile,
  LedgerEntry,
  TransactionItem,
  WalletBalance,
} from "@/types/account";

function formatINR(amount: string | null | undefined): string {
  if (amount === null || amount === undefined) {
    return "—";
  }
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {
    return amount;
  }
  return n.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
}

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-IN");
}

/** Human label per ledger type.
 *
 * An exhaustive Record, not a `.replace(/_/g, " ")`: the raw wire values
 * read badly ("payout initiated", "promo clawback") and, more usefully,
 * an exhaustive map means the next ledger type added to the backend
 * fails the build here instead of rendering a bare enum name at a user.
 */
const ENTRY_LABEL: Record<LedgerEntry["entry_type"], string> = {
  top_up: "Wallet top-up",
  trip_earning: "Trip earning",
  commission_deduction: "Platform commission",
  payout_initiated: "Payout to bank",
  payout_failed_refund: "Payout failed — refunded",
  adjustment_credit: "Adjustment (credit)",
  adjustment_debit: "Adjustment (debit)",
  transfer_in: "Received transfer",
  transfer_out: "Sent transfer",
  trip_charge: "Trip charge",
  promo_credit: "Promotion credit",
  promo_clawback: "Promotion reversed",
  cancellation_charge: "Cancellation charge",
  cancellation_compensation: "Cancellation compensation",
};

export function PaymentsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [wallet, setWallet] = useState<WalletBalance | null>(null);
  const [txns, setTxns] = useState<TransactionItem[]>([]);
  const [profile, setProfile] = useState<AgentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [w, t, p] = await Promise.all([
          accountApi.wallet(accessToken, { limit: 25 }, signal),
          accountApi.transactions(accessToken, { limit: 25 }, signal),
          accountApi.profile(accessToken, signal),
        ]);
        setWallet(w);
        setTxns(t.items);
        setProfile(p);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        setError(
          e instanceof ApiError ? e.problem.detail : "Could not load payments.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const c = new AbortController();
    void load(c.signal);
    return () => {
      c.abort();
    };
  }, [load]);

  const payoutAccount =
    profile?.bank_accounts.find((b) => b.is_primary) ??
    profile?.bank_accounts[0] ??
    null;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader title="Payments" showOwnerSwitcher={false} />
      <main className="mx-auto max-w-5xl space-y-4 p-6">
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void load();
            }}
          >
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
        </div>

        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Wallet balance</CardDescription>
              <CardTitle className="text-2xl">
                <Wallet className="mr-1 inline h-5 w-5" />
                {loading ? "—" : formatINR(wallet?.balance)}
              </CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription>On its way to your bank</CardDescription>
              <CardTitle className="text-2xl">
                {loading ? "—" : formatINR(wallet?.pending_payouts_total)}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              Already taken out of the balance above — not an extra amount.
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Payout account</CardDescription>
              <CardTitle className="text-base">
                <Building2 className="mr-1 inline h-4 w-4" />
                {payoutAccount === null
                  ? "Not set"
                  : `••••${payoutAccount.account_number_last_4}`}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              {payoutAccount === null ? (
                // p68: "a verified payout account is required before
                // withdrawal or settlement release" -- so an absent one
                // is a blocker to state, not a blank to leave.
                <>Add one in the mobile app before withdrawing.</>
              ) : (
                <>
                  {payoutAccount.bank_name} ·{" "}
                  {payoutAccount.verification_status === "verified"
                    ? "verified"
                    : `${payoutAccount.verification_status} — withdrawals blocked until verified`}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* --- p61: recent transactions, gross and commission separate --- */}
        <Card>
          <CardHeader>
            <CardTitle>Trip transactions</CardTitle>
            <CardDescription>
              What the customer paid and what the platform took, per trip.
              Kept as separate columns so both reconciliations work off the
              same table.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!loading && txns.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                No completed trips yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Completed</th>
                      <th className="py-2 pr-4">Route</th>
                      <th className="py-2 pr-4">Gross</th>
                      <th className="py-2 pr-4">Commission</th>
                      <th className="py-2 pr-4">Commission status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t) => (
                      <tr key={t.trip_id} className="border-b last:border-b-0">
                        <td className="py-2 pr-4 text-xs text-muted-foreground">
                          {formatDateTime(t.completed_at)}
                        </td>
                        <td className="py-2 pr-4">
                          {t.pickup_district} → {t.drop_district}
                        </td>
                        <td className="py-2 pr-4">{formatINR(t.gross)}</td>
                        <td className="py-2 pr-4 text-muted-foreground">
                          {formatINR(t.commission)}
                        </td>
                        <td className="py-2 pr-4 capitalize">
                          {t.commission_collection_status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* --- the ledger itself ---------------------------------------- */}
        <Card>
          <CardHeader>
            <CardTitle>Wallet activity</CardTitle>
            <CardDescription>
              Every movement, most recent first
              {wallet !== null && wallet.entries_total > wallet.entries.length
                ? ` — showing ${String(wallet.entries.length)} of ${String(wallet.entries_total)}.`
                : "."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!loading && (wallet?.entries.length ?? 0) === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                Nothing in the wallet yet.
              </div>
            ) : (
              <div className="space-y-1 text-sm">
                {(wallet?.entries ?? []).map((e) => {
                  const credit = !e.amount.startsWith("-");
                  return (
                    <div
                      key={e.id}
                      className="flex items-center justify-between border-b py-2 last:border-b-0"
                    >
                      <div>
                        <div>{ENTRY_LABEL[e.entry_type]}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatDateTime(e.created_at)}
                          {e.counterparty_name !== null && (
                            <> · {e.counterparty_name}</>
                          )}
                        </div>
                      </div>
                      <span
                        className={
                          credit
                            ? "font-medium text-success"
                            : "font-medium text-muted-foreground"
                        }
                      >
                        {credit ? "+" : ""}
                        {formatINR(e.amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
